import {
  Avatar,
  Box,
  Button,
  Card,
  Flex,
  Text,
} from "@radix-ui/themes";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { buildLoginRedirectPath } from "../../../authentication/core/client/login-redirect";
import {
  getApiUserErrorMessage,
  getMeteredCheckoutUrl,
  isUnauthorizedApiError,
} from "../../../shared/api/nest-api";
import { useDeviceIdQuery } from "../../../shared/queries/use-device-id-query";
import { isWebPlatform } from "../../../shared/utils/environment";
import { useUnit } from "../../hooks/use-unit";
import { MeteredPricing } from "./metered-pricing.component";
import { TariffPricing } from "./tariff-pricing.component";
import { UnitRentAppRequired } from "./unit-rent-app-required";

export const UnitRentCheckout: React.FC = () => {
  const unit = useUnit();
  const { data: deviceId } = useDeviceIdQuery();
  const navigate = useNavigate();

  const mutation = useMutation({
    mutationFn: () =>
      getMeteredCheckoutUrl(unit.id, {
        deviceId,
      }),
    onSuccess: (data) => {
      window.location.href = data.checkoutUrl;
    },
    onError: (error) => {
      if (isUnauthorizedApiError(error)) {
        void navigate(
          buildLoginRedirectPath(
            window.location.pathname + window.location.search,
            unit.stationSlug,
          ),
          { replace: true },
        );
        return;
      }

      const errorMessage = getApiUserErrorMessage(error);
      toast.error(
        errorMessage ?? "Leider ist ein unerwarteter Fehler aufgetreten.",
      );
    },
  });

  const handleBookClick = () => {
    mutation.mutate();
  };

  if (unit.hasBluetoothLock && isWebPlatform()) {
    return <UnitRentAppRequired />;
  }

  return (
    <Flex direction="column" gap="4">
      <Text>Jetzt ganz unkompliziert und sicher buchen.</Text>

      <Card>
        <Flex gap="3" align="center">
          <Avatar
            className="unit"
            size="3"
            src={unit.imageUrl}
            radius="large"
            fallback={unit.name.charAt(0)}
            style={{ objectFit: "cover" }}
          />
          <Box>
            <Text as="div" size="2" weight="bold">
              {unit.name}
            </Text>
            <Text as="div" size="2" color="gray">
              {unit.description}
            </Text>
          </Box>
        </Flex>
      </Card>

      {unit.tariffPricesInCents ? (
        <TariffPricing tariffPricesInCents={unit.tariffPricesInCents} />
      ) : (
        <MeteredPricing />
      )}

      <Button
        type="button"
        onClick={handleBookClick}
        loading={mutation.isPending}
      >
        Jetzt buchen
      </Button>
    </Flex>
  );
};
